/**
 * useOperationsCenterRibbon — Ribbon do Painel (Centro Operacional)
 *
 * Abas principais: Ao Vivo / Capacidade / Fluxo / Resultados / Executivo.
 * Subvisões ficam na página, abaixo do ribbon.
 * Filtros e simulação via afterGroups (composto na página).
 */

import React, { useMemo } from 'react';
import type { RibbonConfig } from '../types/ribbon';
import { OpsScreenModeTabs } from '../components/ops/OpsScreenModeTabs';
import type { KaizenSubTab } from '../components/ops/kaizen/opsKaizenShared';
import type { BenchmarkSubTab } from '../components/ops/benchmark/benchmarkShared';

export type OpsScreen = 'live' | 'capacity' | 'flow' | 'results' | 'executive';
export type CapacitySubView = 'heatmap' | 'team' | 'load';
export type FlowSubView = 'overview' | 'bottlenecks';

/** Legado — sub-abas internas ainda consumidas pelos painéis Kaizen. */
export type { KaizenSubTab, BenchmarkSubTab };

export interface OperationsCenterRibbonDeps {
  opsScreen?: OpsScreen;
  onOpsScreenChange?: (screen: OpsScreen) => void;
  /** Slot direito (filtros + simulação) — montar com JSX na página .tsx */
  afterGroups?: React.ReactNode;
}

export interface OpsNavState {
  screen: OpsScreen;
  capacity: CapacitySubView;
  flow: FlowSubView;
}

/** Aceita as abas novas e os query params antigos (heatmap, benchmark, analysis). */
export function readOpsNav(search: string): OpsNavState {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  const view = params.get('view');

  if (tab === 'heatmap') {
    return { screen: 'capacity', capacity: 'heatmap', flow: 'overview' };
  }
  if (tab === 'benchmark') {
    const capacity: CapacitySubView = view === 'load' || view === 'carga'
      ? 'load'
      : view === 'heatmap'
        ? 'heatmap'
        : 'team';
    return { screen: 'capacity', capacity, flow: 'overview' };
  }
  if (tab === 'analysis') {
    if (view === 'financial' || view === 'resultado' || view === 'results') {
      return { screen: 'results', capacity: 'heatmap', flow: 'overview' };
    }
    return {
      screen: 'flow',
      capacity: 'heatmap',
      flow: view === 'losses' || view === 'bottlenecks' || view === 'gargalos' ? 'bottlenecks' : 'overview',
    };
  }
  if (tab === 'capacity') {
    const capacity: CapacitySubView = view === 'team' || view === 'equipe' || view === 'occupation'
      ? 'team'
      : view === 'load' || view === 'carga'
        ? 'load'
        : 'heatmap';
    return { screen: 'capacity', capacity, flow: 'overview' };
  }
  if (tab === 'flow' || tab === 'fluxo') {
    return {
      screen: 'flow',
      capacity: 'heatmap',
      flow: view === 'bottlenecks' || view === 'gargalos' || view === 'losses' ? 'bottlenecks' : 'overview',
    };
  }
  if (tab === 'results' || tab === 'resultados') {
    return { screen: 'results', capacity: 'heatmap', flow: 'overview' };
  }
  if (tab === 'executive') {
    return { screen: 'executive', capacity: 'heatmap', flow: 'overview' };
  }
  return { screen: 'live', capacity: 'heatmap', flow: 'overview' };
}

export function useOperationsCenterRibbon(deps: OperationsCenterRibbonDeps): RibbonConfig {
  const screen = deps.opsScreen ?? 'live';

  return useMemo<RibbonConfig>(() => ({
    moduleId: 'operations',
    groups: [],
    slots: {
      beforeGroups: (
        <OpsScreenModeTabs
          screen={screen}
          onChange={deps.onOpsScreenChange ?? (() => {})}
        />
      ),
      afterGroups: deps.afterGroups,
    },
  }), [
    screen,
    deps.onOpsScreenChange,
    deps.afterGroups,
  ]);
}
